import React, { createContext, useContext, useState, useMemo, useEffect, useCallback } from 'react';
import storage, { flushPendingPushes } from '../services/storage';
import {
  ensureDefaultAdmin,
  loadAuthSession,
  saveAuthSession,
  saveAuthUsers,
  normalizeEmail,
  supabaseLogin,
  supabaseLogout,
  loadProfiles,
  hydrateUserData,
  updateUserRole,
  updateUserProfile,
  createSupabaseUser,
  clearMustChangePassword,
  deleteSupabaseUser,
  AUTH_SESSION_KEY,
  AUTH_USERS_KEY,
} from '../auth';
import { supabase, isConfigured } from '../services/supabase';
import { pullTeamActivitiesIntoLocal } from '../services/leadActivitiesCloud';
const AuthContext = createContext(null);

const DEFAULT_PERMISSIONS = {
  viewPipeline: true,
  viewRevenue: false,
  viewImport: false,
  viewSettings: false,
  deleteLeads: false,
  exportData: false,
  createLeads: true,
};

const loadUserDataRef = { current: null };

/** CRM-App registriert hier die Funktion zum Neuladen nach Login/Hydrate. */
export function registerCrmDataLoader(fn) {
  loadUserDataRef.current = fn;
}

export function AuthProvider({ children }) {
  const [authUsers, setAuthUsers] = useState(() => (isConfigured ? [] : ensureDefaultAdmin()));
  const [authSession, setAuthSession] = useState(() => (isConfigured ? null : loadAuthSession()));
  const [loginError, setLoginError] = useState('');
  const [appReady, setAppReady] = useState(!isConfigured);
  const [syncConflicts, setSyncConflicts] = useState([]);
  const [isHydrated, setIsHydrated] = useState(!isConfigured);

  const currentUser = useMemo(
    () => authUsers.find(u => u.id === authSession?.userId) || null,
    [authUsers, authSession],
  );

  const currentPermissions = useMemo(() => {
    if (!currentUser || currentUser.role === 'admin') {
      return Object.fromEntries(Object.keys(DEFAULT_PERMISSIONS).map(k => [k, true]));
    }
    return { ...DEFAULT_PERMISSIONS, ...(currentUser.permissions || {}) };
  }, [currentUser]);

  useEffect(() => {
    if (!isConfigured) saveAuthUsers(authUsers);
  }, [authUsers]); // eslint-disable-line

  useEffect(() => {
    if (!isConfigured) saveAuthSession(authSession);
  }, [authSession]); // eslint-disable-line

  const loadUserData = useCallback((user) => {
    loadUserDataRef.current?.(user);
  }, []);

  const hydrateAndSetUser = useCallback(async (userId) => {
    const profiles = await loadProfiles();
    let myProfile = profiles.find(p => p.id === userId);

    if (!myProfile) {
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (authUser) {
          myProfile = {
            id: authUser.id,
            name: authUser.user_metadata?.name || authUser.email?.split('@')[0] || '',
            email: authUser.email || '',
            role: 'admin',
            title: '',
            avatar: '',
            mustChangePassword: false,
            createdAt: authUser.created_at,
          };
          await supabase.from('profiles').upsert({
            id: myProfile.id,
            name: myProfile.name,
            email: myProfile.email,
            role: myProfile.role,
          });
          profiles.push(myProfile);
        }
      } catch { /* ignore */ }
    }

    if (!myProfile) {
      setLoginError('Profil nicht gefunden. Bitte wende dich an den Administrator.');
      window.dispatchEvent(new CustomEvent('crm-error', {
        detail: {
          code: 'LOGIN_NO_PROFILE',
          message: 'Kein Profil gefunden für diesen Benutzer.',
          detail: `user_id=${userId}`,
        },
      }));
      return;
    }

    storage.set(AUTH_SESSION_KEY, { userId: myProfile.id, email: myProfile.email, role: myProfile.role });
    storage.set(AUTH_USERS_KEY, profiles);

    try {
      await flushPendingPushes();
      const conflicts = await hydrateUserData(userId);
      setSyncConflicts(conflicts || []);
    } catch (err) {
      setSyncConflicts([]);
      window.dispatchEvent(new CustomEvent('crm-error', {
        detail: {
          code: 'HYDRATE_FAIL',
          message: 'Cloud-Daten konnten nicht geladen werden. Lokale Daten werden verwendet.',
          detail: err?.message || String(err),
        },
      }));
    }

    if (isConfigured) {
      try {
        await pullTeamActivitiesIntoLocal();
        window.dispatchEvent(new CustomEvent('crm-activities-changed', { detail: { leadId: '__all__' } }));
      } catch (e) {
        console.warn('[CRM] Team-Aktivitäten:', e?.message || e);
      }
    }

    setAuthUsers(profiles);
    setAuthSession({ userId: myProfile.id, email: myProfile.email, role: myProfile.role });
    loadUserData(myProfile);
    setIsHydrated(true);
  }, [loadUserData]);

  useEffect(() => {
    if (!isConfigured) return;

    async function init() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) await hydrateAndSetUser(session.user.id);
      } catch (e) {
        console.error('[init] Supabase init failed:', e);
      } finally {
        setAppReady(true);
      }
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        setAuthSession(null);
        setAuthUsers([]);
        setSyncConflicts([]);
      }
    });

    init();
    return () => subscription.unsubscribe();
  }, [hydrateAndSetUser]);

  const handleLogin = useCallback(async (email, password) => {
    setLoginError('');
    if (isConfigured) {
      try {
        const { session } = await supabaseLogin(email.trim(), password.trim());
        if (session) {
          await hydrateAndSetUser(session.user.id);
        } else {
          setLoginError('Anmeldung fehlgeschlagen. Bitte erneut versuchen.');
        }
      } catch (e) {
        const msg = e?.message || '';
        if (msg.toLowerCase().includes('invalid login credentials')) {
          setLoginError('E-Mail oder Passwort falsch.');
        } else if (msg.toLowerCase().includes('email not confirmed')) {
          setLoginError('E-Mail-Adresse noch nicht bestätigt. Bitte prüfe dein Postfach.');
        } else {
          setLoginError(`Anmeldefehler: ${msg}`);
        }
      }
    } else {
      const mail = normalizeEmail(email);
      const user = authUsers.find(u => normalizeEmail(u.email) === mail && u.password === password.trim());
      if (!user) {
        setLoginError('E-Mail oder Passwort ist falsch.');
        return;
      }
      if (user.disabled) {
        setLoginError('Dieser Account wurde gesperrt. Bitte wende dich an deinen Administrator.');
        return;
      }
      // Record last login timestamp
      setAuthUsers(prev => prev.map(u => u.id === user.id ? { ...u, lastLogin: new Date().toISOString() } : u));
      setAuthSession({ userId: user.id, email: user.email, role: user.role || 'viewer' });
      loadUserData(user);
    }
  }, [authUsers, hydrateAndSetUser, loadUserData]);

  const handleLogout = useCallback(async () => {
    if (isConfigured) await supabaseLogout();
    setAuthSession(null);
    setSyncConflicts([]);
  }, []);

  const handleUsersChange = useCallback(async (updaterOrList) => {
    const prev = authUsers;
    let next = typeof updaterOrList === 'function' ? updaterOrList(prev) : updaterOrList;

    if (isConfigured) {
      const added = next.filter(u => !prev.find(p => p.id === u.id));
      for (const u of added) {
        try {
          const created = await createSupabaseUser(u.email, u.password || 'ChangeMe123!', u.name, u.role || 'viewer');
          // Replace temporary Date.now() id with real Supabase UUID
          if (created?.id && created.id !== u.id) {
            next = next.map(nu => nu.id === u.id ? { ...nu, id: created.id } : nu);
          }
        } catch (e) {
          console.warn('Supabase user creation failed:', e.message);
        }
      }
      const removed = prev.filter(p => !next.find(u => u.id === p.id));
      for (const u of removed) {
        deleteSupabaseUser(u.id).catch(() => {});
      }
      for (const u of next) {
        const old = prev.find(p => p.id === u.id);
        if (!old) continue;
        if (old.role !== u.role) updateUserRole(u.id, u.role).catch(() => {});
        if (old.name !== u.name || old.title !== u.title || old.avatar !== u.avatar) {
          updateUserProfile(u.id, { name: u.name, title: u.title, avatar: u.avatar }).catch(() => {});
        }
      }
    }

    setAuthUsers(next);
    if (!isConfigured) saveAuthUsers(next);
  }, [authUsers]);

  const value = useMemo(() => ({
    authUsers,
    setAuthUsers,
    authSession,
    currentUser,
    currentPermissions,
    loginError,
    appReady,
    syncConflicts,
    setSyncConflicts,
    isHydrated,
    handleLogin,
    handleLogout,
    handleUsersChange,
    hydrateAndSetUser,
    loadUserData,
    clearMustChangePassword,
    isConfigured,
  }), [
    authUsers,
    authSession,
    currentUser,
    currentPermissions,
    loginError,
    appReady,
    syncConflicts,
    isHydrated,
    handleLogin,
    handleLogout,
    handleUsersChange,
    hydrateAndSetUser,
    loadUserData,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
